import { useEffect, useMemo, useState } from 'react';
import { lookupPubChemSmiles } from '@peruselab/api-client/pubchem';
import { readReactionText, textOf } from '@peruselab/structure-editor/chemistry/reactionText';
import { reactionTexts } from '@peruselab/structure-editor/domain/reactionBoxes';
import type { ReactionZone, ReactionZoneBox } from '@peruselab/structure-editor/domain/reactionGraph';
import type { DrawingDocument } from '@peruselab/structure-editor/model';

/** A reagent's lookup, by the name typed: found (with its structure) or not (and why). */
export type ReagentLookup =
  | { readonly status: 'looking' }
  | { readonly status: 'found'; readonly smiles: string }
  | { readonly status: 'not-found'; readonly message: string };

/** One reagent typed by the arrow, where, and what its lookup found. */
export interface ReadReagent {
  readonly name: string;
  readonly catalyst: boolean;
  readonly zone: 'above' | 'below';
  readonly annotationId: string;
  readonly lookup: ReagentLookup;
}

/**
 * ADR 0014 amendment (#1137, owner) -- what the reaction's texts say: conditions (to fill the
 * Conditions card's empty fields), notes, and reagents with their lookups. The first text to give
 * a condition gives it.
 */
export interface ReactionTextReading {
  readonly conditions: {
    readonly temperatureC?: number;
    readonly pressureAtm?: number;
    readonly durationS?: number;
    readonly yieldPercent?: number;
  };
  readonly notes: readonly string[];
  readonly reagents: readonly ReadReagent[];
}

export const EMPTY_READING: ReactionTextReading = { conditions: {}, notes: [], reagents: [] };

/** Each name is looked up once a session (PubChem by name or formula, as #1064 allows). */
const lookups = new Map<string, Promise<ReagentLookup>>();

export function lookupReagent(name: string): Promise<ReagentLookup> {
  const key = name.toLowerCase();
  let pending = lookups.get(key);
  if (!pending) {
    pending = lookupPubChemSmiles({ name }).then(
      (smiles): ReagentLookup => ({ status: 'found', smiles }),
      (error: unknown): ReagentLookup => ({
        status: 'not-found',
        message: error instanceof Error ? error.message : String(error)
      })
    );
    lookups.set(key, pending);
  }
  return pending;
}

/** The reading of `document`'s texts, given what each name's lookup has found so far. */
export function readingOf(
  document: DrawingDocument,
  shown: Readonly<Record<ReactionZone, ReactionZoneBox>> | null,
  found: ReadonlyMap<string, ReagentLookup>
): ReactionTextReading {
  const conditions: { temperatureC?: number; pressureAtm?: number; durationS?: number; yieldPercent?: number } = {};
  const notes: string[] = [];
  const reagents: ReadReagent[] = [];
  for (const { annotation, zone } of reactionTexts(document, shown)) {
    const read = readReactionText(textOf(annotation));
    for (const [key, value] of Object.entries(read.conditions) as [keyof typeof conditions, number][]) {
      conditions[key] ??= value;
    }
    notes.push(...read.notes);
    for (const { name, catalyst } of read.reagents) {
      reagents.push({
        name,
        catalyst,
        zone,
        annotationId: annotation.id,
        lookup: found.get(name.toLowerCase()) ?? { status: 'looking' }
      });
    }
  }
  return { conditions, notes, reagents };
}

/** How long typing must pause before a new name is looked up. */
const LOOKUP_PAUSE_MS = 600;

/** #1137 -- the reading, live: it follows the paper, and looks up new names as typing pauses. */
export function useReactionTextReading(
  document: DrawingDocument,
  shown: Readonly<Record<ReactionZone, ReactionZoneBox>> | null
): ReactionTextReading {
  const [found, setFound] = useState<ReadonlyMap<string, ReagentLookup>>(new Map());
  const reading = useMemo(() => (shown ? readingOf(document, shown, found) : EMPTY_READING), [document, shown, found]);
  const pendingNames = reading.reagents.filter((reagent) => reagent.lookup.status === 'looking').map((r) => r.name);
  const pendingKey = pendingNames.join('\n');
  useEffect(() => {
    if (!pendingKey) return;
    let cancelled = false;
    const timer = globalThis.setTimeout(() => {
      for (const name of pendingKey.split('\n')) {
        void lookupReagent(name).then((lookup) => {
          if (!cancelled) setFound((current) => new Map(current).set(name.toLowerCase(), lookup));
        });
      }
    }, LOOKUP_PAUSE_MS);
    return () => {
      cancelled = true;
      globalThis.clearTimeout(timer);
    };
  }, [pendingKey]);
  return reading;
}
